import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/EmptyState';
import { Plus, Edit, Eye, Search, UserCircle2, Trash2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ImageUploader, fileUrl } from '@/components/Uploaders';
import { useAuth } from '@/lib/auth';

const TINGKAT_OPTIONS = [
  'Wali Kota', 'Wakil Wali Kota', 'Sekda', 'Asisten', 'Staf Ahli',
  'Kepala Dinas', 'Kepala Badan', 'Kepala Bagian', 'Camat', 'Lurah',
];

export default function PejabatManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', tingkat: 'all', opd: 'all', is_active: 'all' });
  const [modal, setModal] = useState({ open: false, mode: 'create', target: null });
  const empty = {
    nip: '', nama: '', gelar_depan: '', gelar_belakang: '',
    jabatan: '', tingkat_jabatan: 'Kepala Dinas',
    opd_id: '', pangkat: '', golongan: '', phone: '', whatsapp_number: '', email: '',
    foto_url: '', is_active: true,
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.tingkat !== 'all') params.set('tingkat_jabatan', filters.tingkat);
      if (filters.opd !== 'all') params.set('opd_id', filters.opd);
      if (filters.is_active !== 'all') params.set('is_active', filters.is_active === 'active' ? 'true' : 'false');
      const [p, o] = await Promise.all([api.get(`/pejabat?${params.toString()}`), api.get('/opd')]);
      setItems(p.data);
      setOpds(o.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.search, filters.tingkat, filters.opd, filters.is_active]);

  const openCreate = () => { setForm(empty); setModal({ open: true, mode: 'create', target: null }); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setModal({ open: true, mode: 'edit', target: p }); };

  const save = async () => {
    if (!form.nama || !form.jabatan || !form.nip) {
      toast.error('Mohon lengkapi NIP, Nama, Jabatan');
      return;
    }
    try {
      if (modal.mode === 'create') {
        await api.post('/pejabat', form);
        toast.success('Pejabat berhasil ditambahkan');
      } else {
        await api.patch(`/pejabat/${modal.target.id}`, form);
        toast.success('Pejabat berhasil diperbarui');
      }
      setModal({ open: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/pejabat/${p.id}`, { is_active: !p.is_active });
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Nonaktifkan pejabat ${p.nama}? Pejabat tidak akan dihapus permanen, hanya dinonaktifkan.`)) return;
    try {
      await api.delete(`/pejabat/${p.id}`);
      toast.success('Pejabat dinonaktifkan');
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'operator';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Master Data Pejabat"
        subtitle="Kelola data pejabat Pemkot Makassar yang menjadi referensi seluruh agenda dan SPPD."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Master Data' }, { label: 'Pejabat' }]}
        action={canEdit && <Button onClick={openCreate} data-testid="add-pejabat-button" className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Tambah Pejabat</Button>}
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4 grid md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cari nama, jabatan, NIP…" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" />
          </div>
          <Select value={filters.tingkat} onValueChange={(v) => setFilters({ ...filters, tingkat: v })}>
            <SelectTrigger><SelectValue placeholder="Tingkat Jabatan" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tingkat</SelectItem>
              {TINGKAT_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.is_active} onValueChange={(v) => setFilters({ ...filters, is_active: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Nonaktif</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="Belum ada pejabat" icon={UserCircle2} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="pejabat-table">
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-16">Foto</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead className="hidden md:table-cell">Jabatan</TableHead>
                  <TableHead className="hidden lg:table-cell">OPD</TableHead>
                  <TableHead className="hidden xl:table-cell w-32">Tingkat</TableHead>
                  <TableHead className="w-24">Aktif</TableHead>
                  <TableHead className="text-right w-32">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="hover:bg-slate-50">
                    <TableCell>
                      {p.foto_url ? (
                        <img src={fileUrl(p.foto_url)} alt={p.nama} className="w-10 h-10 rounded-full object-cover border" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold text-sm">{p.nama?.[0] || '?'}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link to={`/manajemen/pejabat/${p.id}`} className="hover:text-red-600">
                        <div className="font-semibold text-slate-800">{p.gelar_depan} {p.nama} {p.gelar_belakang}</div>
                        <div className="text-xs text-slate-500 font-mono">NIP. {p.nip || '-'}</div>
                      </Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{p.jabatan}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-600">{p.opd_nama}</TableCell>
                    <TableCell className="hidden xl:table-cell"><span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{p.tingkat_jabatan || '-'}</span></TableCell>
                    <TableCell><Switch checked={p.is_active !== false} onCheckedChange={() => canEdit && toggleActive(p)} disabled={!canEdit} /></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/manajemen/pejabat/${p.id}`)}><Eye className="w-4 h-4" /></Button>
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Edit className="w-4 h-4" /></Button>}
                      {user?.role === 'admin' && <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{modal.mode === 'create' ? 'Tambah Pejabat' : `Edit Pejabat: ${modal.target?.nama}`}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-1 flex flex-col items-center">
              <ImageUploader value={form.foto_url} onChange={(url) => setForm({ ...form, foto_url: url })} label="Upload Foto" size={140} />
            </div>
            <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
              <div><Label>NIP *</Label><Input value={form.nip} onChange={(e) => setForm({ ...form, nip: e.target.value })} placeholder="19xxxxxxxxxxxxxxxx" /></div>
              <div><Label>Nama Lengkap *</Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></div>
              <div><Label>Gelar Depan</Label><Input value={form.gelar_depan} onChange={(e) => setForm({ ...form, gelar_depan: e.target.value })} placeholder="H. / Drs. / Dr." /></div>
              <div><Label>Gelar Belakang</Label><Input value={form.gelar_belakang} onChange={(e) => setForm({ ...form, gelar_belakang: e.target.value })} placeholder="S.E., M.Si." /></div>
              <div className="md:col-span-2"><Label>Jabatan *</Label><Input value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} placeholder="Kepala Dinas Pendidikan" /></div>
              <div>
                <Label>Tingkat Jabatan</Label>
                <Select value={form.tingkat_jabatan} onValueChange={(v) => setForm({ ...form, tingkat_jabatan: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TINGKAT_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>OPD</Label>
                <Select value={form.opd_id || 'none'} onValueChange={(v) => setForm({ ...form, opd_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Pilih OPD" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">- Tidak ada -</SelectItem>
                    {opds.map((o) => <SelectItem key={o.id} value={o.id}>{o.nama}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Pangkat</Label><Input value={form.pangkat} onChange={(e) => setForm({ ...form, pangkat: e.target.value })} placeholder="Pembina Tingkat I" /></div>
              <div><Label>Golongan</Label><Input value={form.golongan} onChange={(e) => setForm({ ...form, golongan: e.target.value })} placeholder="IV/b" /></div>
              <div><Label>No. HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+62 8xx-xxxx-xxxx" /></div>
              <div>
                <Label className="flex items-center gap-1.5">No. WhatsApp <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold uppercase">WA</span></Label>
                <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="628xxxxxxxxxx (untuk notifikasi WA)" />
              </div>
              <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false })}>Batal</Button>
            <Button onClick={save} data-testid="save-pejabat-button" className="bg-[#DC2626] hover:bg-[#B91C1C]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
